import type { Language } from '@seek/melways-sites';
import { useTranslations } from '@vocab/react';
import {
  Text,
  Box,
  Columns,
  Column,
  Stack,
  CheckboxStandalone,
  Inline,
  ButtonIcon,
  IconEnlarge,
  IconMail,
  IconArrow,
  MenuItem,
  MenuRenderer,
  IconChevron,
  Button,
  Strong,
  Bleed,
  useResponsiveValue,
} from 'braid-design-system';
import { observer } from 'mobx-react';
import { type FC, useState } from 'react';

import { usePageTabs } from 'src/App/CandidateManagementPage/PageTabs/usePageTabs';
import SortOrderDropdown from 'src/components/app/SortOrderDropdown';
import { useUserActionStatus } from 'src/components/common/UserActionStatusContext';
import type { PageConfig } from 'src/libs/PageConfig';
import {
  type Bucket,
  type BUCKETS,
  SelectAllStatus,
} from 'src/stores/JobApplicationStore/types';
import useBucketLabel from 'src/utils/hooks/useBucketLabel';

import translations from './.vocab';
import { BulkEmailDialog } from './BulkEmailDialog';
import { BulkMoveDialog } from './BulkMoveDialog';

import * as styles from './ActionToolbar.css';

export interface ActionToolbarProps {
  selectedCount: number;
  currentTotalOfJobApplications: number;
  bucket: BUCKETS;
  sortBy: string;
  hasSearchText: boolean;
  hasQuestion: boolean;
  onSortByChange: (params: { sortBy: string }) => Promise<void>;
  selectedFilterCount: number;
  matchingApplicationsCount: number;
  selectAllStatus: SelectAllStatus;
  selectAll: (props: {
    isCheckboxChecked: boolean;
    clickSourceForTracking: 'checkbox' | 'menuitem';
  }) => void;
  deselectAll: () => void;
  selectAllJobApplicationsInBucket: (params: {
    shouldShowLoading: boolean;
    onCompleted: () => void;
  }) => void;
  onExpandApplicationCardState: () => void;
  onCollapseApplicationCardState: () => void;
  onOpenBulkEmailDialog: () => void;
  onOpenBulkMoveDialog: () => boolean;
  onClickSelectionMenu: () => void;
  updateStatusForSelectedApplications: (status: Bucket) => Promise<boolean>;
  getSelectedJobApplicationNames: () => string[];
  getSelectedJobApplicationsNameToBulkMoveLimit: () => string[];
  onClickViewBulkMoveJobApps: (status: Bucket) => void;
  jobTitle: string;
  advertiserName: string;
  userName: string;
  userEmail: string;
  sendProspectEmail: (props: {
    subject: string;
    message: string;
    enableReplies: boolean;
    language: Language;
    addCustomError: PageConfig['dataDogRum']['addCustomError'];
  }) => Promise<boolean>;
  addCustomError: PageConfig['dataDogRum']['addCustomError'];
  refetchFacets: () => Promise<void>;
}

// We are loading all job applications from the Graph with secondary select all (a.k.a clickSelectAllJobApplicationsInBucket)
// For this reason, Jobs with a large number of applications in the current bucket may take a few seconds to load
// From what we observed, anything higher than 500 records to load take a time long enough to be noticed by users (internet speed may cause result to vary)
const LOAD_ALL_THRESHOLD_FOR_LOADER = 500;

export const ActionToolbar: FC<ActionToolbarProps> = observer(
  ({
    currentTotalOfJobApplications,
    selectedCount,
    bucket,
    sortBy,
    hasSearchText,
    hasQuestion,
    onSortByChange,
    selectAllStatus,
    selectedFilterCount,
    matchingApplicationsCount,
    selectAll,
    deselectAll,
    selectAllJobApplicationsInBucket,
    onExpandApplicationCardState,
    onCollapseApplicationCardState,
    onOpenBulkEmailDialog,
    onOpenBulkMoveDialog,
    onClickSelectionMenu,
    updateStatusForSelectedApplications,
    getSelectedJobApplicationNames,
    getSelectedJobApplicationsNameToBulkMoveLimit,
    onClickViewBulkMoveJobApps,
    advertiserName,
    jobTitle,
    sendProspectEmail,
    userEmail,
    userName,
    addCustomError,
    refetchFacets,
  }) => {
    const { t } = useTranslations(translations);

    const { getFilterDrawerState } = useUserActionStatus('filter-drawer-state');
    const { isStickyOnDesktop: isPageTabsSticky } = usePageTabs();

    const isTablet =
      useResponsiveValue()({
        mobile: false,
        tablet: true,
        desktop: false,
        wide: false,
      }) ?? false;

    const isDesktopWithFilterCollapse =
      useResponsiveValue()({
        mobile: false,
        tablet: false,
        desktop: getFilterDrawerState() === 'expanded',
        wide: false,
      }) ?? false;

    const { getApplicationCardState, toggleApplicationCardState } =
      useUserActionStatus('application-card-state');

    const [isLoadingAllJobApplications, setIsLoadingAllJobApplications] =
      useState(false);
    const { getLabelFromBucket } = useBucketLabel();
    const [isBulkMoveDialogOpen, setIsBulkMoveDialogOpen] = useState(false);
    const [isBulkEmailDialogOpen, setIsBulkEmailDialogOpen] = useState(false);
    const bucketLabel = getLabelFromBucket(bucket);
    const selectAllLabel = hasSearchText
      ? t('All results')
      : t('Bucket', { bucket: bucketLabel });

    const isCheckboxChecked =
      selectAllStatus === SelectAllStatus.SelectAll ||
      selectAllStatus === SelectAllStatus.SelectAllIndividually ||
      selectAllStatus === SelectAllStatus.SelectAllInFolder;

    const isBulkActionEnabled =
      selectedCount > 0 && !isLoadingAllJobApplications;

    const collapseButton =
      isTablet || isDesktopWithFilterCollapse ? (
        <ButtonIcon
          id="toolbar-collapse-button-icon"
          label={t('Collapse')}
          icon={<IconEnlarge active />}
          onClick={() => {
            onCollapseApplicationCardState();
            toggleApplicationCardState();
          }}
          size={isTablet ? 'small' : 'standard'}
        />
      ) : (
        <Button
          tone="neutral"
          variant="transparent"
          icon={<IconEnlarge active />}
          onClick={() => {
            onCollapseApplicationCardState();
            toggleApplicationCardState();
          }}
          size="small"
        >
          {t('Collapse')}
        </Button>
      );

    const expandButton =
      isTablet || isDesktopWithFilterCollapse ? (
        <ButtonIcon
          id="toolbar-expand-button-icon"
          label={t('Expand')}
          icon={<IconEnlarge />}
          onClick={() => {
            onExpandApplicationCardState();
            toggleApplicationCardState();
          }}
          size={isTablet ? 'small' : 'standard'}
        />
      ) : (
        <Button
          tone="neutral"
          variant="transparent"
          icon={<IconEnlarge />}
          onClick={() => {
            onExpandApplicationCardState();
            toggleApplicationCardState();
          }}
          size="small"
        >
          {t('Expand')}
        </Button>
      );

    const applicationCardStateButton =
      getApplicationCardState() === 'expanded' ? collapseButton : expandButton;

    const showMatchingApplicationsText = selectedFilterCount > 0;

    return (
      <>
        <Box
          paddingLeft="small"
          paddingRight="medium"
          paddingY={['none', 'small', 'small', 'xxsmall']}
          className={{
            [styles.sticky]: true,
            [styles.topBorderRadius]: true,
            [styles.dropShadow]: true,
            [styles.belowPageTabs]: isPageTabsSticky,
          }}
          data={{ testid: 'action-toolbar' }}
          background="surface"
        >
          <Stack space={['none', 'small', 'small', 'medium']}>
            <Columns space="small" alignY="center">
              <Column>
                <Inline space="small" alignY="center">
                  <Inline space="none" alignY="center">
                    <Box className={styles.checkbox}>
                      <CheckboxStandalone
                        id="select-all"
                        disabled={
                          selectAllStatus === SelectAllStatus.Empty ||
                          isLoadingAllJobApplications
                        }
                        aria-label={selectAllLabel}
                        checked={isCheckboxChecked}
                        onChange={() =>
                          selectAll({
                            isCheckboxChecked,
                            clickSourceForTracking: 'checkbox',
                          })
                        }
                        size="small"
                      />
                    </Box>

                    {selectedCount > 0 && (
                      <Text>{t('selectedCount', { selectedCount })}</Text>
                    )}
                  </Inline>

                  {/* FIXME: NVL - not sure whether still need it in NVL */}
                  <Bleed left={selectedCount > 0 ? 'none' : 'medium'}>
                    <MenuRenderer
                      trigger={(triggerProps) => (
                        <Box
                          {...triggerProps}
                          aria-label={t('Selection options')}
                          userSelect="none"
                          cursor="pointer"
                          onClick={(e) => {
                            onClickSelectionMenu();
                            triggerProps.onClick(e);
                          }}
                        >
                          <IconChevron direction="down" size="small" />
                        </Box>
                      )}
                    >
                      <MenuItem
                        onClick={() =>
                          selectAll({
                            isCheckboxChecked,
                            clickSourceForTracking: 'menuitem',
                          })
                        }
                      >
                        {isCheckboxChecked ? (
                          <Strong>{t('Select all on page')}</Strong>
                        ) : (
                          t('Select all on page')
                        )}
                      </MenuItem>

                      <MenuItem
                        onClick={() => {
                          const shouldShowLoading =
                            currentTotalOfJobApplications >
                            LOAD_ALL_THRESHOLD_FOR_LOADER;
                          if (shouldShowLoading) {
                            setIsLoadingAllJobApplications(true);
                          }
                          selectAllJobApplicationsInBucket({
                            shouldShowLoading,
                            onCompleted: () =>
                              setIsLoadingAllJobApplications(false),
                          });
                        }}
                      >
                        {hasSearchText
                          ? t(
                              'Select all totalJobApplicationCount applications',
                              {
                                totalJobApplicationCount:
                                  matchingApplicationsCount,
                              },
                            )
                          : t('Select all statusFolder', {
                              statusFolder: getLabelFromBucket(bucket, 'lower'),
                            })}
                      </MenuItem>

                      <MenuItem onClick={() => deselectAll()}>
                        {t('Deselect all')}
                      </MenuItem>
                    </MenuRenderer>
                  </Bleed>

                  {isBulkActionEnabled && (
                    <>
                      <ButtonIcon
                        icon={<IconMail />}
                        id="toolbar-mail-button-icon"
                        label={t('Send email')}
                        onClick={() => {
                          setIsBulkEmailDialogOpen(true);
                          onOpenBulkEmailDialog();
                        }}
                        size={isTablet ? 'small' : 'standard'}
                      />

                      <ButtonIcon
                        icon={<IconArrow direction="right" />}
                        id="toolbar-move-button-icon"
                        label={t('Move to folder')}
                        onClick={() => {
                          if (onOpenBulkMoveDialog()) {
                            setIsBulkMoveDialogOpen(true);
                          }
                        }}
                        size={isTablet ? 'small' : 'standard'}
                      />
                    </>
                  )}
                </Inline>
              </Column>

              {!isTablet &&
              !isDesktopWithFilterCollapse &&
              showMatchingApplicationsText ? (
                <Column>
                  <Text maxLines={1} size="small" align="right">
                    {t(
                      '<Strong>matchingApplicationsCount</Strong> matching applications',
                      {
                        matchingApplicationsCount,
                        Strong: (children) => <Strong>{children}</Strong>,
                      },
                    )}
                  </Text>
                </Column>
              ) : null}

              {hasQuestion ? (
                <Column width="content">{applicationCardStateButton}</Column>
              ) : null}

              <Column width="content">
                <Box display="flex" justifyContent="flexEnd">
                  <SortOrderDropdown
                    sortBy={sortBy}
                    hasSearchText={hasSearchText}
                    hasQuestion={hasQuestion}
                    onSortByChange={onSortByChange}
                    align="right"
                  />
                </Box>
              </Column>
            </Columns>

            {(isTablet || isDesktopWithFilterCollapse) &&
            showMatchingApplicationsText ? (
              <Box style={{ paddingLeft: '15px' }}>
                <Text maxLines={1} size="small" align="left">
                  {t(
                    '<Strong>matchingApplicationsCount</Strong> matching applications',
                    {
                      matchingApplicationsCount,
                      Strong: (children) => <Strong>{children}</Strong>,
                    },
                  )}
                </Text>
              </Box>
            ) : null}
          </Stack>
        </Box>
        <BulkMoveDialog
          updateStatusForSelectedApplications={async (status) => {
            const success = await updateStatusForSelectedApplications(status);
            if (success) {
              setIsBulkMoveDialogOpen(false);
            }
            await refetchFacets();
            return success;
          }}
          getSelectedJobApplicationNames={
            getSelectedJobApplicationsNameToBulkMoveLimit
          }
          currentStatus={bucket}
          isOpen={isBulkMoveDialogOpen}
          onClose={() => {
            setIsBulkMoveDialogOpen(false);
          }}
          onClickToastView={onClickViewBulkMoveJobApps}
        />
        <BulkEmailDialog
          open={isBulkEmailDialogOpen}
          onClose={() => {
            setIsBulkEmailDialogOpen(false);
          }}
          getSelectedJobApplicationNames={getSelectedJobApplicationNames}
          jobTitle={jobTitle}
          advertiserName={advertiserName}
          userName={userName}
          userEmail={userEmail}
          sendProspectEmail={sendProspectEmail}
          addCustomError={addCustomError}
        />
      </>
    );
  },
);
